import React, { useMemo, useState } from 'react';
import { FormattedMessage, FormattedDate } from 'react-intl';

import {
  add,
  endOfDay,
  endOfWeek,
  endOfYear,
  isToday,
  startOfDay,
} from 'date-fns';

import {
  Select,
  MenuItem,
  IconButton,
  Typography,
  Button,
  ButtonGroup,
  Box,
  SelectChangeEvent,
} from '@mui/material';
import {
  BarChart,
  ChevronLeft,
  ChevronRight,
  ShowChart,
  SsidChart,
  StackedBarChart,
} from '@mui/icons-material';

import { useSelector } from '../../store/utils';

import { Row } from '../../atoms/row';

import { ChartDataVariableId } from '../../utils/statistics';
import { statisticsConfig } from '../../../../core/src/shared/statistics-config';
import { Plus } from '../../atoms/icons/plus';

export enum Variant {
  Line = 'line',
  LineStacked = 'line-stacked',
  Bar = 'bar',
  BarStacked = 'bar-stacked',
}

export enum Mode {
  Day = 'Day',
  Week = 'Week',
  Year = 'Year',
}

export enum Aggregate {
  Disabled = 'Disabled',
  Hour = 'Hour',
  Day = 'Day',
  Month = 'Month',
}

type Options =
  | {
      parent: true;
      variableId: undefined;
      initialVariant?: Variant;
    }
  | {
      parent: false;
      variableId: ChartDataVariableId;
      initialVariant?: Variant;
    };

export const useChartBoxModeSelect = (options: Options) => {
  const [to, setTo] = useState(new Date());
  const [mode, setMode] = useState(Mode.Day);
  const [variant, setVariant] = useState(
    options.initialVariant ?? Variant.Line
  );

  const { subStats } = options.variableId
    ? statisticsConfig[options.variableId]
    : { subStats: undefined };

  const [premium] = useSelector(
    ({ variables: { PREMIUM } }) => [PREMIUM] as const
  );

  const showSubStats = premium && (options.parent || Boolean(subStats));

  const handleModeChange = (e: SelectChangeEvent<Mode>) => {
    setTo(new Date());
    setMode(e.target.value as Mode);
  };

  const handleVariantChange = (variant: Variant) => {
    setVariant(variant);
  };

  const from = useMemo(() => {
    const today = isToday(to);

    let newValue = to;
    if (mode === Mode.Day) {
      newValue = startOfDay(to);
      if (today) newValue = add(to, { days: -1 });
    } else if (mode === Mode.Week) {
      newValue = add(to, { days: -7 });
      if (today && [Variant.Bar, Variant.BarStacked].includes(variant))
        newValue = startOfDay(newValue);
    } else if (mode === Mode.Year) {
      newValue = add(to, { years: -1 });
      if (today && [Variant.Bar, Variant.BarStacked].includes(variant))
        newValue = startOfDay(newValue);
    }

    return newValue;
  }, [mode, to, variant]);

  const onPrev = () => {
    const today = isToday(to);

    let newValue = to;
    if (mode === Mode.Day) {
      newValue = add(to, { days: -1 });
      if (today) newValue = endOfDay(newValue);
    }
    if (mode === Mode.Week) {
      newValue = add(to, { days: -7 });
      if (today) newValue = endOfWeek(newValue, { weekStartsOn: 1 });
    }
    if (mode === Mode.Year) {
      newValue = add(to, { years: -1 });
      if (today) newValue = endOfYear(newValue);
    }

    setTo(newValue);
  };

  const onNext = () => {
    let newValue = to;
    if (mode === Mode.Day) {
      newValue = add(to, { days: 1 });
      if (isToday(newValue)) newValue = new Date();
    }
    if (mode === Mode.Week) {
      newValue = add(to, { days: 7 });
      if (newValue >= new Date()) newValue = new Date();
    }
    if (mode === Mode.Year) {
      newValue = add(to, { years: 1 });
      if (newValue >= new Date()) newValue = new Date();
    }

    setTo(newValue);
  };

  const nextDisabled = useMemo(() => {
    return to > startOfDay(new Date());
  }, [to, mode]);

  const formattedRange = useMemo(() => {
    if (mode === Mode.Day && isToday(to)) {
      return (
        <FormattedMessage
          id="chart.range.today"
          defaultMessage="Posledních 24{nbsp}hodin"
          values={{ nbsp: '\u00A0' }}
        />
      );
    }

    if (mode === Mode.Day) {
      return <FormattedDate value={from} month="short" day="numeric" />;
    }

    if (mode === Mode.Week && isToday(to)) {
      return (
        <FormattedMessage
          id="chart.range.this_week"
          defaultMessage="Poslední týden"
        />
      );
    }

    return (
      <>
        <Box sx={{ whiteSpace: 'nowrap' }} component="span">
          <FormattedDate
            value={from}
            month="short"
            day="numeric"
            year={mode === Mode.Year ? 'numeric' : undefined}
          />
        </Box>
        {' - '}
        <Box sx={{ whiteSpace: 'nowrap' }} component="span">
          <FormattedDate
            value={to}
            month="short"
            day="numeric"
            year={mode === Mode.Year ? 'numeric' : undefined}
          />
        </Box>
      </>
    );
  }, [from, to, mode]);

  const chartVariantSelect = (
    <ButtonGroup variant="outlined">
      <Button
        onClick={() => handleVariantChange(Variant.Line)}
        variant={variant === Variant.Line ? 'contained' : 'outlined'}
      >
        <ShowChart />
      </Button>
      <Button
        onClick={() => handleVariantChange(Variant.Bar)}
        variant={variant === Variant.Bar ? 'contained' : 'outlined'}
      >
        <BarChart />
      </Button>
      {showSubStats && (
        <>
          <Button
            onClick={() => handleVariantChange(Variant.LineStacked)}
            variant={variant === Variant.LineStacked ? 'contained' : 'outlined'}
            sx={{ position: 'relative' }}
          >
            <SsidChart />

            <Plus
              sx={{ position: 'absolute', top: -6, right: -6, zIndex: 10 }}
            />
          </Button>
          <Button
            onClick={() => handleVariantChange(Variant.BarStacked)}
            variant={variant === Variant.BarStacked ? 'contained' : 'outlined'}
            sx={{ position: 'relative' }}
          >
            <StackedBarChart />

            <Plus
              sx={{ position: 'absolute', top: -6, right: -6, zIndex: 10 }}
            />
          </Button>
        </>
      )}
    </ButtonGroup>
  );

  return {
    mode,
    setMode,
    from,
    to,
    variant,
    filters: (
      <Row
        sx={{
          flexWrap:
            showSubStats && options.parent
              ? { xs: 'wrap', sm: 'nowrap' }
              : 'nowrap',
        }}
      >
        <Select value={mode} onChange={handleModeChange} sx={{ mr: 0.5 }}>
          <MenuItem value={Mode.Day}>
            <FormattedMessage id="charts.mode.day" defaultMessage="Den" />
          </MenuItem>
          <MenuItem value={Mode.Week}>
            <FormattedMessage id="charts.mode.week" defaultMessage="Týden" />
          </MenuItem>
          <MenuItem value={Mode.Year}>
            <FormattedMessage id="charts.mode.year" defaultMessage="Rok" />
          </MenuItem>
        </Select>

        <IconButton size="small" sx={{ '& svg': { fontSize: 26 } }}>
          <ChevronLeft onClick={onPrev} />
        </IconButton>
        <Typography variant="body1" sx={{ mx: 0.5 }}>
          {formattedRange}
        </Typography>
        <IconButton
          size="small"
          sx={{ '& svg': { fontSize: 26 } }}
          disabled={nextDisabled}
        >
          <ChevronRight onClick={onNext} />
        </IconButton>

        <Box
          sx={{
            display: {
              xs: !options.parent ? 'none' : 'block',
              md: 'block',
            },
            ml: 'auto',
            mt: { xs: showSubStats ? 1 : 0, sm: 0 },
            flexBasis: {
              xs: showSubStats ? '100%' : 'auto',
              sm: 'auto',
            },
          }}
        >
          {chartVariantSelect}
        </Box>
      </Row>
    ),
    footer: (
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {chartVariantSelect}
      </Box>
    ),
  };
};
